import { IComboBoxOption } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapMedium } from 'globalStyles';
import AuditTrailAction from 'models/auditTrailAction';
import AuditTrailEntry from 'models/auditTrailEntry';
import User from 'models/user';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAuditActions, apiGetAudits } from 'services/Api/auditService';
import { apiRequest } from 'services/Auth/authConfig';
import FilterAuditTrail from './FilterAuditTrail';
import SingleAudit from './SingleAudit';

interface IAuditTrailProps {
  auditTrailId?: number;
}

const AuditTrail = (props: IAuditTrailProps) => {
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [auditTrails, setAuditTrails] = useState<AuditTrailEntry[]>([]);
  const { t } = useTranslation(['translation', 'auditTrail']);
  const [actionList, setActionList] = useState<IComboBoxOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [selectedActionID, setSelectedActionID] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [showDialog, setShowDialog] = useState<string>('');

  useEffect(() => {
    loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      if (isLoading || !props.auditTrailId) {
        return;
      }
      setIsLoading(true);

      //load Data
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const auditTrails = await apiGetAudits(
        props.auditTrailId,
        selectedUser?.id,
        undefined,
        startDate,
        endDate,
        accessToken,
      );
      const auditActions = await apiGetAuditActions(accessToken);
      auditActions.sort((a, b) => a.name.localeCompare(b.name));

      //   sort on creation date descending
      auditTrails.entries.sort((a, b) => {
        return a.created > b.created ? -1 : 1;
      });
      setAuditTrails(auditTrails.entries);
      setActionList(
        auditActions.map((audit: AuditTrailAction) => {
          return { key: audit.auditTrailActionId, text: audit.name };
        }),
      );
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilter = async () => {
    try {
      if (isLoading || !props.auditTrailId) {
        return;
      }
      setIsLoading(true);

      //load Data
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const auditTrails = await apiGetAudits(
        props.auditTrailId,
        selectedUser?.id,
        selectedActionID,
        startDate,
        endDate,
        accessToken,
      );

      //   sort on creation date descending
      auditTrails.entries.sort((a, b) => {
        return a.created > b.created ? -1 : 1;
      });
      setAuditTrails(auditTrails.entries);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (auditTrails) {
      if (selectedUser || selectedActionID || startDate || endDate) {
        applyFilter();
      } else {
        loadData();
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActionID, selectedUser, startDate, endDate]);

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  } else if (!props.auditTrailId) {
    return null;
  } else
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <FilterAuditTrail
            selectedUser={selectedUser}
            onSelectedUserChange={setSelectedUser}
            selectedAction={selectedActionID}
            onSelectedActionChange={setSelectedActionID}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            actionList={actionList}
            showDialog={showDialog}
            onShowDialogChange={setShowDialog}
          />
        </IPStack.Item>
        <ScrollableStackItem minHeight={100}>
          {auditTrails.length === 0 && (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPText>{t('auditTrail:NoAudits')}</IPText>
            </IPStack>
          )}
          {auditTrails.length > 0 &&
            auditTrails.map((audit: AuditTrailEntry) => {
              return <SingleAudit key={audit.auditTrailEntryId} audit={audit} />;
            })}
        </ScrollableStackItem>
      </IPStack>
    );
};
export default AuditTrail;
